/* eslint-disable no-prototype-builtins */
import React, { useState, useCallback, useEffect, useRef } from 'react'
import axios from 'axios'
import { useNavigate, useParams } from 'react-router-dom'
import { useDrag, useDrop } from 'react-dnd'

import Card from '@mui/material/Card'
import CardHeader from '@mui/material/CardHeader'
import CardMedia from '@mui/material/CardMedia'
import CardContent from '@mui/material/CardContent'
import CardActions from '@mui/material/CardActions'
import Collapse from '@mui/material/Collapse'
import Avatar from '@mui/material/Avatar'
import IconButton from '@mui/material/IconButton'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { Link } from '@mui/material'
import StarIcon from '@mui/icons-material/Star'
import SendIcon from '@mui/icons-material/Send'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'

import { ExpandMore } from '../../../../helpers/cardHelpers'
import { getPayload, getTokenFromLocalStorage, userIsAuthenticated } from '../../../../helpers/storage'
import { positionChangeHeightSm, positionChangeWidthSm, swipelyticsPink, swipelyticsTurquoise } from '../../../../helpers/variableDefaults'
import useWindowDimensions from '../../../../helpers/windowDimensions'
import { modifyInDatabase } from '../../../../helpers/modifyInDatabase'
import { dragAndDropIconElement } from '../../../../helpers/buttons'
import { standardErrorContainer } from '../../../../helpers/errors'

const EditPromptCard = ({ promptItem, datingProfile, setDatingProfile, promptItemIndex, loading, tapDisabled, handleSelectItem }) => {
  
  // Window Dimensions
  const { height, width } = useWindowDimensions()

  // Payload
  const payload = getPayload()

  // Navigate
  const navigate = useNavigate()

  // Params
  const { estateId, personId } = useParams()

  // URL Path
  const urlPath = window.location.pathname

  // Tracks whether the card is expanded
  const [expanded, setExpanded] = useState(false)

  // Edit Item Error
  const [editItemError, setEditItemError] = useState(false)

  // Drag and Drop
  const nullRef = useRef(null) // Disable drag and drop on mobile
  const ref = useRef(null)

  // Executes the drag/drop for the heir draft planning
  const movePromptItem = (dragIndex, hoverIndex, datingProfile, setDatingProfile, modifyInDatabase) => {
    // console.log('dragIndex ->', dragIndex)
    // console.log('hoverIndex ->', hoverIndex)
    // console.log('prompt item ->', promptItem)

    const draggedPromptItem = { ...datingProfile.bio.prompts[dragIndex] }
    // console.log('draggedMediaItem ->', draggedMediaItem)

    const newPromptArray = [ ...datingProfile.bio.prompts ]
    // console.log('beginning ->', newMediaArray)

    // Delete old item
    newPromptArray.splice(dragIndex, 1)
    // console.log('after delete ->', newPromptArray)

    // Add it at the hover index
    newPromptArray.splice(hoverIndex, 0, draggedPromptItem)
    // console.log('after add ->', newPromptArray)

    const newBioObj = { ...datingProfile.bio, prompts: newPromptArray }


    // Set State
    // console.log('before set ->', newBioObj)
    setDatingProfile({ ...datingProfile, bio: newBioObj })

  }

  // Drop Handler
  const [{ handlerId }, drop] = useDrop({
    
    // Component Type (lower case)
    accept: 'editpromptitem',
    
    // Monitoring
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      }
    },

    // Hover Handler
    hover(item, monitor) {
      if (!ref.current) {
        return
      }
      // console.log('drop item ->', item)
      const dragIndex = item.promptItemIndex // Start index
      // console.log('dragIndex ->', dragIndex)
      const hoverIndex = promptItemIndex // Index that's being hovered over
      // console.log('hoverIndex ->', hoverIndex)
      // console.log('media length ->', datingProfile.media.length)

      // Don't replace items with themselves; don't place an image at an index below the length the prompt items array
      if (dragIndex === hoverIndex || hoverIndex >= (datingProfile.bio.prompts.length) || hoverIndex < 0) {
        // console.log('out of scope')
        return
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect()
      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      // Determine mouse position
      const clientOffset = monitor.getClientOffset()
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top

      // Time to actually perform the action
      console.log('movePromptItem should run')
      movePromptItem(dragIndex, hoverIndex, datingProfile, setDatingProfile, modifyInDatabase)

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.promptItemIndex = hoverIndex
    },
  })

  // Drag Handler
  const [{ isDragging }, drag, preview] = useDrag({
    
    // Component Type (lower case)
    type: 'editpromptitem',

    // Item properties to retrieve
    item: () => {
      // console.log('is dragging')
      return { promptItemIndex }
    },

    // Monitor
    collect: (monitor) => ({
      // console.log('is monitoring')
      isDragging: monitor.isDragging(),
    }),
  })

  // Combine it all — Set the drag and drop to the ref
  drag(drop(ref))


  return (
    <Box 
      ref={loading.prompts ? nullRef : ref} // Drag/drop enabled
      onClick={(e) => handleSelectItem(e, promptItemIndex)}
      disabled={tapDisabled()}
      sx={{ 
        width: '100%',
        cursor: tapDisabled() ? '' : 'pointer',
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center', 
      }}
    >
      {/* Prompt */}
      <Typography
        sx={{
          px: 2, pt: 1.5,
          width: '100%',
          fontWeight: 'bold',
        }}
      >
        {datingProfile.bio.prompts[promptItemIndex].prompt}
      </Typography>

      {/* Response */}
      <Typography
        sx={{
          px: 2, pb: 1.5,
          width: '100%',
        }}
      >
        {datingProfile.bio.prompts[promptItemIndex].response}
      </Typography>
    </Box>
  )
}

export default EditPromptCard